import { ReactNode, useState } from 'react'
import { useOnboarding } from '../hooks/useOnboarding'
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Menu,
  MenuItem,
  IconButton,
  Avatar,
  Tooltip,
  Stack,
  Drawer,
  List,
  ListItemButton,
  ListItemText,
  Divider,
} from '@mui/material'
import { AccountCircle, Menu as MenuIcon } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { UserRole, SCHOOL_BROWSE_ENABLED_FOR_STUDENTS, SUPPORT_EMAIL } from '@tnf-recruit/shared'
import logo from '../assets/logo.png'
import { useAuth } from '../hooks/useAuth'
import { useUser } from '../hooks/useUser'

interface LayoutProps {
  children: ReactNode
}

export const Layout = ({ children }: LayoutProps) => {
  const { user, signOut } = useAuth()
  const { data: dbUser } = useUser()
  const navigate = useNavigate()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleClose = () => {
    setAnchorEl(null)
  }

  const handleLogout = () => {
    signOut().finally(() => navigate('/'))
    handleClose()
  }

  const userRole = dbUser?.role
  const { onboardingComplete } = useOnboarding()
  const navDisabled = !onboardingComplete

  const navItems: { label: string; path: string }[] = [{ label: 'Dashboard', path: '/dashboard' }]

  if (userRole === UserRole.RECRUITER || userRole === UserRole.ADMIN) {
    navItems.push({ label: 'Browse Athletes', path: '/browse' })
  }

  if (userRole === UserRole.RECRUITER) {
    navItems.push({ label: 'Subscription', path: '/subscription' })
  }

  if (
    userRole === UserRole.ADMIN ||
    (userRole === UserRole.STUDENT && SCHOOL_BROWSE_ENABLED_FOR_STUDENTS)
  ) {
    navItems.push({ label: 'Browse Schools', path: '/schools' })
  }

  navItems.push({ label: 'Profile', path: '/profile' })

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default', minHeight: '100vh' }}>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: 'primary.dark',
          color: 'common.white',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ gap: 2 }}>
            <Box
              component="img"
              src={logo}
              alt="T&F All-Star Recruiting"
              sx={{
                height: 40,
                width: 40,
                objectFit: 'contain',
                cursor: 'pointer',
              }}
              onClick={() => !navDisabled && navigate('/dashboard')}
            />
            <Typography
              variant="h6"
              component="div"
              sx={{
                fontWeight: 700,
                cursor: 'pointer',
                display: { xs: 'none', sm: 'block' },
              }}
              onClick={() => !navDisabled && navigate('/dashboard')}
            >
              T&F All-Star Recruiting
            </Typography>

            <Box sx={{ flexGrow: 1 }} />

            <Tooltip title={navDisabled ? 'Complete onboarding to access' : ''}>
              <span>
                <IconButton
                  size="large"
                  color="inherit"
                  sx={{ display: { xs: 'inline-flex', md: 'none' } }}
                  onClick={() => setMobileOpen(true)}
                  disabled={navDisabled}
                >
                  <MenuIcon />
                </IconButton>
              </span>
            </Tooltip>

            <Stack
              direction="row"
              spacing={1}
              sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}
            >
              {navItems.map(item => (
                <Tooltip key={item.path} title={navDisabled ? 'Complete onboarding to access' : ''}>
                  <span>
                    <Button
                      color="inherit"
                      onClick={() => navigate(item.path)}
                      disabled={navDisabled}
                      sx={{ fontWeight: 600 }}
                    >
                      {item.label}
                    </Button>
                  </span>
                </Tooltip>
              ))}
            </Stack>

            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              sx={{ ml: 1 }}
            >
              <Avatar
                src={user?.picture}
                sx={{
                  width: 36,
                  height: 36,
                  bgcolor: 'warning.main',
                  color: 'warning.contrastText',
                  border: '2px solid rgba(255,255,255,0.25)',
                }}
              >
                <AccountCircle />
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
              keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <Typography variant="body2" color="text.secondary">
                  {user?.email}
                </Typography>
              </MenuItem>
              <Divider />
              <MenuItem
                onClick={() => {
                  handleClose()
                  navigate('/settings')
                }}
              >
                Settings
              </MenuItem>
              <MenuItem component="a" href={`mailto:${SUPPORT_EMAIL}`} onClick={handleClose}>
                Contact Support
              </MenuItem>
              <MenuItem onClick={handleLogout}>Log Out</MenuItem>
            </Menu>
          </Toolbar>
        </Container>
      </AppBar>

      <Drawer anchor="right" open={mobileOpen} onClose={() => setMobileOpen(false)}>
        <Box sx={{ width: 280 }} role="presentation">
          <Box sx={{ p: 2 }}>
            <Typography variant="subtitle2" color="text.secondary">
              Navigation
            </Typography>
          </Box>
          <Divider />
          <List>
            {navItems.map(item => (
              <ListItemButton
                key={item.path}
                disabled={navDisabled}
                onClick={() => {
                  setMobileOpen(false)
                  navigate(item.path)
                }}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ))}
          </List>
          <Divider />
          <List>
            <ListItemButton
              onClick={() => {
                setMobileOpen(false)
                navigate('/settings')
              }}
            >
              <ListItemText primary="Settings" />
            </ListItemButton>
            <ListItemButton
              component="a"
              href="mailto:support@recruitallstar.com"
              onClick={() => setMobileOpen(false)}
            >
              <ListItemText primary="Contact Support" />
            </ListItemButton>
            <ListItemButton
              onClick={() => {
                setMobileOpen(false)
                handleLogout()
              }}
            >
              <ListItemText primary="Log Out" />
            </ListItemButton>
          </List>
        </Box>
      </Drawer>

      <Container maxWidth="xl" sx={{ py: { xs: 3, md: 5 } }}>
        {children}
      </Container>
    </Box>
  )
}
